import { useMemo, useState } from "react";
import { DoubleSidebar, useGlobal, useAccount } from "@vojomo-core/shared";
import {
  MobileHeader,
  MobileNavigation
} from "@vojomo-core/shared/src/components/Mobile";
import { mainLinksData, createLinksData } from "../config/routeConfig";
import { Helmet } from "react-helmet";
import { Feedback } from "@vojomo-core/shared/src/components/Feedback/Feedback";
import { Tag } from "antd";
import { useDoubleSidebar } from "@vojomo-core/shared";

interface BaseLayoutProps {
  children: React.ReactNode;
  pageTitle?: string;
  contactFooter?: boolean;
  showMobileHeader?: boolean;
}

const BaseLayout = ({
  children,
  pageTitle = "Vojomo",
  contactFooter = true,
  showMobileHeader = true
}: BaseLayoutProps): React.ReactNode => {
  const { isCollapsed } = useDoubleSidebar();
  const { isMobile } = useGlobal();
  const { vojomoAccount } = useAccount();
  const linksData = createLinksData();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const isCoordinator =
    (vojomoAccount?.company_coordinator_companies?.length ?? 0) > 0 ||
    !!vojomoAccount?.is_admin ||
    vojomoAccount?.company === "Vojomo";

  const filteredMainLinksData = useMemo(
    () =>
      mainLinksData.filter(
        (item) => item.title !== "Coordinator" || isCoordinator
      ),
    [isCoordinator]
  );
  const contentOffset = isMobile ? "0px" : isCollapsed ? "100px" : "250px";

  return (
    <>
      <Helmet>
        <title>{`Vojomo${
          pageTitle !== "Vojomo" ? " | " + pageTitle : ""
        }`}</title>
      </Helmet>

      {/* Mobile Header */}
      {isMobile && showMobileHeader && (
        <MobileHeader
          onMenuClick={() => setMobileNavOpen(true)}
          showLogo={true}
        />
      )}

      {/* Mobile Navigation Drawer */}
      {isMobile && (
        <MobileNavigation
          isOpen={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          mainLinksData={filteredMainLinksData}
          linksData={linksData}
        />
      )}

      {/* Desktop Sidebar */}
      {!isMobile && (
        <DoubleSidebar
          mainLinksData={filteredMainLinksData}
          activeName={pageTitle}
          linksData={linksData}
        />
      )}
      <div
        style={{
          marginLeft: contentOffset,
          transition: "margin-left 0.1s ease",
          paddingTop: isMobile && showMobileHeader ? "56px" : "0px",
          paddingBottom: contactFooter ? "40px" : "0px",
          paddingLeft: isMobile || isCollapsed ? "0px" : "30px",
          paddingRight: isMobile ? "0px" : "15px",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {children}

        {contactFooter && (
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: contentOffset,
              right: 0,
              transition: "left 0.1s ease",
              padding: isMobile ? "0.5rem 0" : "0.75rem 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              zIndex: 5,
            }}
          >
            <div style={{ pointerEvents: "auto" }}>
              <Tag
                style={{
                  paddingInline: isMobile ? "8px" : "12px",
                  borderRadius: "15px",
                  fontSize: isMobile ? "12px" : "14px",
                  margin: 0,
                }}
                color="#ef4269"
              >
                private beta
              </Tag>
            </div>
            <div
              style={{
                position: "absolute",
                right: isMobile ? "1rem" : "2rem",
                pointerEvents: "auto",
              }}
            >
              <Feedback />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BaseLayout;
